import React from 'react';
import { defineMessages, FormattedNumber, useIntl } from 'react-intl';
import classNames from 'classnames';

import ArrowButton, { ArrowPosition, ArrowDirection } from 'hellospa/foundation/control/arrow-button';
import Body from 'hellospa/foundation/typography/body';
import styles from './table.scss';

const DEBOUNCE_TIME = process.env.NODE_ENV === 'test'
  ? 10
  : 3000;

const messages = defineMessages({
  prev: {
    id: '',
    description: 'Label for pagination previous button',
    defaultMessage: 'Previous',
  },
  next: {
    id: '',
    description: 'Label for pagination next button',
    defaultMessage: 'Next',
  },
  page: {
    id: '',
    description: 'Label for page number input',
    defaultMessage: 'Page',
  },
});

export type PaginationPropTypes = {
  currentPage: number,
  numPages: number,
  isLoading: boolean,
  onPageChange: (page: number) => void,
  className?: string,
}

function Pagination({
  currentPage,
  numPages,
  onPageChange,
  isLoading,
  className,
}: PaginationPropTypes) {
  const intl = useIntl();
  const [currentValue, setCurrentValue] = React.useState(currentPage);
  const pageRef = React.useRef() as React.MutableRefObject<HTMLInputElement>;
  const pageChangeTrigger = React.useRef<NodeJS.Timeout | null>(null);

  const numMaxPageDigits = React.useMemo(() => {
    return Math.floor(Math.log10(numPages)) + 1;
  }, [numPages]);

  const clearPageChangeTrigger = React.useCallback(() => {
    // Clear the timeout if one exists.
    if (pageChangeTrigger.current) {
      clearTimeout(pageChangeTrigger.current);

      pageChangeTrigger.current = null;
    }
  }, []);

  const getPageNumber = React.useCallback(() => {
    if (!pageRef.current) return null;

    const val = pageRef.current.value;
    const cleanedVal = val.replace(/[^\d]/g, '');
    const slicedVal = cleanedVal.slice(0, numMaxPageDigits);

    if (slicedVal.length) {
      return parseInt(slicedVal, 10);
    }

    return null;
  }, [numMaxPageDigits, pageRef]);

  const changePage = React.useCallback((newPage) => {
    if (isLoading) return;

    setCurrentValue(newPage);
    clearPageChangeTrigger();
    onPageChange(newPage);
  }, [isLoading, clearPageChangeTrigger, onPageChange]);

  const debouncedChangePage = React.useCallback((newPage) => {
    clearPageChangeTrigger();

    pageChangeTrigger.current = setTimeout(() => {
      changePage(newPage);

      if (pageRef.current) {
        pageRef.current.blur();
      }
    }, DEBOUNCE_TIME);
  }, [clearPageChangeTrigger, changePage, pageRef]);

  const onPrevPage = React.useCallback(() => {
    const newPage = currentPage - 1;

    if (currentPage >= 1) {
      changePage(newPage);
    }
  }, [currentPage, changePage]);

  const onNextPage = React.useCallback(() => {
    const newPage = currentPage + 1;

    if (newPage <= numPages) {
      changePage(newPage);
    }
  }, [currentPage, numPages, changePage]);

  const pageChangeRequest = React.useCallback((fn: (newPage: number) => void) => {
    const page = getPageNumber();

    if (page && page !== currentPage && page <= numPages) {
      fn(page);
    }
  }, [currentPage, numPages, getPageNumber]);

  const onPageInputChange = React.useCallback((evt) => {
    setCurrentValue(evt.target.value);

    pageChangeRequest((newPage) => {
      debouncedChangePage(newPage);
    });
  }, [pageChangeRequest, debouncedChangePage]);

  const onPageInputSubmit = React.useCallback((evt) => {
    evt.preventDefault();

    pageChangeRequest((newPage) => {
      changePage(newPage);

      if (pageRef.current) {
        pageRef.current.blur();
      }
    });
  }, [pageChangeRequest, changePage, pageRef]);

  const onPageInputFocus = React.useCallback((evt) => {
    evt.target.select();
  }, []);

  React.useEffect(() => {
    setCurrentValue(currentPage);
  }, [currentPage]);

  return (
    <div className={classNames(styles.pagination, className)}>
      <form
        className={styles.paginationForm}
        onSubmit={onPageInputSubmit}
      >
        <ArrowButton
          data-qa-ref="pagination-previous"
          className={styles.paginationArrow}
          position={ArrowPosition.Left}
          direction={ArrowDirection.Left}
          animated={false}
          onClick={onPrevPage}
          disabled={isLoading || currentPage === 1}
          aria-label={intl.formatMessage(messages.prev)}
          type="button"
        >
          {/* Intentionally blank */}
        </ArrowButton>
        <input
          className={styles.paginationField}
          type="text"
          value={currentValue}
          ref={pageRef}
          onFocus={onPageInputFocus}
          onChange={onPageInputChange}
          size={numMaxPageDigits}
          maxLength={numMaxPageDigits}
          disabled={isLoading || numPages === 1}
          aria-label={intl.formatMessage(messages.page)}
          data-qa-ref="pagination-form" />
        <Body data-qa-ref="current-page">
          <span className={styles.paginationSlash}>/</span><FormattedNumber value={numPages} />
        </Body>
        <ArrowButton
          data-qa-ref="pagination-next"
          className={styles.paginationArrow}
          position={ArrowPosition.Right}
          direction={ArrowDirection.Right}
          animated={false}
          onClick={onNextPage}
          disabled={isLoading || currentPage === numPages}
          aria-label={intl.formatMessage(messages.next)}
          type="button"
        >
          {/* Intentionally blank */}
        </ArrowButton>
      </form>
    </div>
  );
}

export default Pagination;
